/**
 * The Code class deals with the code format handling and optimized code lookups.
 *
 * Formats:
 * key     = code.mainNumber.subNumber      // the full key
 * mainKey = code.mainNumber                // the main key
 * code    = code                           // the code
 */
export class CodeData {

    constructor(data = []) {
        this.data = data;
        this.lookup = this.createLookup(data);
        this.lookupOrder = this.createLookupOrder(data);
    }

    /**
     * Create lookup with 'value' or 'value (ref)' if ref is available.
     */
    createLookup(data) {
        return data.reduce((acc, { key, value, ref }) => {
            acc[key] = ref ? `${value} (${ref})` : value;
            return acc;
        }, {});
    }

    createLookupOrder(data) {
        return data.reduce((acc, { key, order }) => {
            acc[key] = order;
            return acc;
        }, {});
    }

    isEmpty() {
        return this.data.length === 0;
    }

    /**
     * Lookup value for the given key, return empty string if no value found.
     */
    lookupValue(key) {
        return this.lookup[key] || '';
    }

    /**
     * Lookup order for the given key, return empty string if no value found.
     */
    lookupOrderValue(key) {
        return this.lookupOrder[key] || '';
    }

    /**
     * Get all codes.
     */
    getCodes() {
        return this.data;
    }

    getCode(key) {
        return (
            this.getCodes().filter(code => key === code.key)
        );
    }

    /**
     * Get value for a given key or return an empty string if no value is found.
     */
    getValue(key) {
        return this.lookupValue(key);
    }

    /**
     * Get order for a given key or return an empty string if no value is found.
     */
    getOrder(key) {
        return this.lookupOrderValue(key);
    }

    // --------------------------------------------------------------------------------------------
    // key and number handling
    // --------------------------------------------------------------------------------------------

    /**
     * Select all keys that start with the given code.
     */
    selectKeys(codePrefix) {
        return this.data
            // Step 1: Filter objects with keys starting with the prefix
            .filter(code => code.key.startsWith(`${codePrefix}.`))
            // Step 2: Map to their `key` property
            .map(code => code.key);
    }

    /**
     * Select all main keys that start with the given code.
     */
    selectMainKeys(code) {
        return this.selectKeys(code).filter(key => key.split(".").length === 2);
    }

    /**
     * Select all sub keys that start with the given main key.
     * Return an empty array if no sub-keys are found.
     */
    selectSubKeys(mainKey) {
        const mainKeyPrefix = `${mainKey}.`;
        return Object.keys(this.lookup)
            .filter(key => key.startsWith(mainKeyPrefix) && key.split(".").length === 3);
    }

    /**
     * Extract all main mumbers for the given list of keys, and sort them numerically.
     * The list of keys must belong to a single code.
     */
    extractMainNumbers(keys) {
        // Extract main numbers and convert them to a Set for uniqueness
        const mainNumbers = new Set(
            keys.map(key => Number(key.split(".")[1]))
        );
        // Convert Set back to array and sort numerically
        return Array.from(mainNumbers).sort((a, b) => a - b);
    }

    /**
     * Extract sub mumbers of the given list of sub keys, and sort them numerically.
     * The list of sub keys must belong to a single main key.
     */
    extractSubNumbers(subKeys) {
        // Extract sub numbers and convert them to a Set for uniqueness
        const mainNumbers = new Set(
            subKeys.map(key => Number(key.split(".")[2]))
        );

        // Convert Set back to array and sort numerically
        return Array.from(mainNumbers).sort((a, b) => a - b);
    }

    /**
     * Generate the next number in a sorted list of numbers. If the list is empty, return 1.
     */
    generateNextNumber(numbers) {
        return numbers.length > 0 ? numbers[numbers.length - 1] + 1 : 0;
    }

    /**
     * Generate the next main key for the given code.
     */
    generateNextMainKey(code) {
        const keys = this.selectKeys(code);
        const mainNumbers = this.extractMainNumbers(keys);
        const nextNumber = this.generateNextNumber(mainNumbers);
        return `${code}.${nextNumber}`;
    }

    /**
     * Generate the next sub key for the given main key.
     */
    generateNextSubKey(mainKey) {
        const subKeys = this.selectSubKeys(mainKey);
        const subNumbers = this.extractSubNumbers(subKeys);
        const nextNumber = this.generateNextNumber(subNumbers);
        return `${mainKey}.${nextNumber}`;
    }

    // --------------------------------------------------------------------------------------------
    // selection handling
    // --------------------------------------------------------------------------------------------

    /**
     * Get options for a select component based on the given key.
     */
    getSelection(key) {
        return (
            this.getCodes()
                // Step 1: Select codes with the given key
                .filter(code => code.key === key)
                // Step 2: Filter active entries only
                .filter(code => code.active === 1)
                // Step 3: Sort by order field
                .sort((a, b) => a.order - b.order)
                // Step 4: Map to MenuItem-compatible structure
                .map(code => ({
                    value: code.key,    // Use key as the value
                    label: code.value   // Use value as the label
                }))
        );
    }

    getSelectionForKeys(keys) {
        return (
            this.getCodes()
                // Step 1: Filter codes that match any of the given keys
                .filter(code => keys.includes(code.key))
                // Step 2: Filter active entries only
                .filter(code => code.active === 1)
                // Step 3: Sort by order field
                .sort((a, b) => a.order - b.order)
                // Step 4: Map to MenuItem-compatible structure
                .map(code => ({
                    value: code.key,                // use key as the value
                    label: this.lookup[code.key]    // use value (ref) or value as label
                }))
        );
    }

    /**
     * Get task (Arbeitskategorie) selection for combox.
     */
    getTaskSelection(naLabel=" ", skipNa=false) {
        let keys = this.selectMainKeys("task");
        const selection = this.getSelectionForKeys(keys);
        if (skipNa) {
            return selection;
        }
        return [{ value: -1, label: naLabel }, ...selection];
    }

    /**
     * Get taks order for the TaskList grouped view. Includes also inactive entries, since old data can
     * still have inactive codes.
     */
    getTaskListGroupOrder() {
        return this.getMainCodes("task")
            .sort((a, b) => a.order - b.order)
            .map(code => ({
                key: code.key,
                label: code.value
            }))
    }


    /**
     * Get sub task (Arbeitsvorgang) selection for combox.
     */
    getSubTaskSelection(key, naLabel=" ", skipNA=false) {
        let keys = this.selectSubKeys(key);
        const selection = this.getSelectionForKeys(keys);
        if (skipNA) {
            return selection;
        }
        return [{ value: -1, label: naLabel }, ...selection];
    }

    /**
     * Get pumb (Pumpe) selection for combox
     */
    getPumpSelection(naLabel="keine Pumpe") {
        let keys = this.selectSubKeys("tool.0");
        const selection = this.getSelectionForKeys(keys);
        return [{ value: -1, label: naLabel }, ...selection];
    }

    // --------------------------------------------------------------------------------------------
    // code administration
    // --------------------------------------------------------------------------------------------

    getCodeForKeys(keys) {
        return (
            this.getCodes()
                // Step 1: Filter codes that match any of the given keys
                .filter(code => keys.includes(code.key))
                // Step 2: Sort by order field
                .sort((a, b) => a.order - b.order)
        );
    }

    getMainCodes(code) {
        let keys = this.selectMainKeys(code);
        return this.getCodeForKeys(keys);
    }

    getSubCodes(mainKey) {
        let keys = this.selectSubKeys(mainKey);
        return this.getCodeForKeys(keys);
    }

    /**
     * Get all keys of the taks with clearing_list set to true (1).
     */
    getSubTaskKeysClearingList() {
        let keys = this.selectSubKeys("task");
        return this.getCodeForKeys(keys)
            .filter(code => code.clearing_list === 1)  // Filter only relevant clearing list entries
            .map(code => code.key);                    // Return only their keys
    }

    /**
     * Get all keys of the taks with material_list set to true (1).
     */
    getSubTaskKeysMaterialList() {
        let keys = this.selectSubKeys("task");
        return this.getCodeForKeys(keys)
            .filter(code => code.material_list === 1)  // Filter only relevant material list entries
            .map(code => code.key);                    // Return only their keys
    }

};
